// ===========================================
// Environment Variable Row Component
// ===========================================
// Displays a single environment variable with view/copy/edit/delete actions
// Values are locked until password is verified

import React, { useState } from 'react';
import { 
  Eye, 
  EyeOff, 
  Copy, 
  Check, 
  Edit, 
  Trash2, 
  Lock,
  Link,
  StickyNote
} from 'lucide-react';

const EnvVariableRow = ({ 
  env,                // The environment variable object
  onCopy,             // Callback to copy value to clipboard
  copiedId,           // ID of currently copied var (for visual feedback)
  onRequestPassword   // Callback to request password for action
}) => {
  const [showValue, setShowValue] = useState(false);
  
  // Handle view toggle - requires password to unlock first
  const handleViewToggle = () => {
    if (!env.unlocked) {
      // Request password to unlock and view
      onRequestPassword('view', env);
    } else {
      setShowValue(!showValue);
    }
  };

  // Handle copy - only works if unlocked
  const handleCopy = () => {
    if (env.unlocked && env.decryptedValue) {
      onCopy(env.decryptedValue, env._id || env.id);
    } else {
      // Need to unlock first
      onRequestPassword('view', env);
    }
  };

  // Get the ID for comparison (handle both id and _id)
  const envId = env._id || env.id;
  
  return (
    <div className="env-row">
      {/* Key and badges */}
      <div className="env-row__key">
        <code>{env.key}</code>
        {env.isSecret && <span className="env-row__secret">Secret</span>}
      </div>
      
      {/* Value display */}
      <div className="env-row__value">
        {env.isSecret && !showValue ? (
          <span className="env-row__masked">••••••••••••</span>
        ) : env.unlocked ? (
          <code>{env.decryptedValue}</code>
        ) : (
          <span className="env-row__locked">
            <Lock size={12} /> Locked
          </span>
        )}
      </div>
      
      {/* Meta: website link and notes indicator */}
      <div className="env-row__meta">
        {env.websiteUrl && (
          <a 
            href={env.websiteUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            title="Open service dashboard"
            className="env-row__link"
          >
            <Link size={14} />
          </a>
        )}
        {env.notes && (
          <span className="env-row__notes-icon" title={env.notes}>
            <StickyNote size={14} />
          </span>
        )}
      </div>
      
      {/* Actions: view, copy, edit, delete */}
      <div className="env-row__actions">
        <button 
          onClick={handleViewToggle} 
          title={env.unlocked ? (showValue ? 'Hide value' : 'Show value') : 'Unlock to view'}
        >
          {showValue && env.unlocked ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
        <button 
          onClick={handleCopy}
          title={env.unlocked ? 'Copy value' : 'Unlock to copy'}
          className={!env.unlocked ? 'disabled' : ''}
        >
          {copiedId === envId ? <Check size={16} /> : <Copy size={16} />}
        </button>
        <button 
          onClick={() => onRequestPassword('edit', env)} 
          title="Edit variable"
        >
          <Edit size={16} />
        </button>
        <button 
          onClick={() => onRequestPassword('delete', env)} 
          title="Delete variable"
          className="env-row__delete-btn"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default EnvVariableRow;

